import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { EmailOutlined, LockOutlined } from "@mui/icons-material";
import axios from "axios";
import { api } from "@api/api";
import IdentidadeVisual from "../../estilos/identidadeVisual";

const AZUL_RECUPERACAO = "#0060ef";

const campoEmailSx = {
  "& .MuiInputLabel-root": {
    color: AZUL_RECUPERACAO,
    fontSize: "16px",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: AZUL_RECUPERACAO,
  },

  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "6px",
    backgroundColor: "rgba(232, 242, 255, 0.45)",
    color: "#182039",
    fontSize: "16px",

    "& fieldset": {
      borderColor: "#78a3ff",
    },

    "&:hover fieldset": {
      borderColor: AZUL_RECUPERACAO,
    },

    "&.Mui-focused fieldset": {
      borderColor: AZUL_RECUPERACAO,
    },
  },

  "& .MuiOutlinedInput-input": {
    fontSize: "inherit",
    lineHeight: 1.25,
    height: "auto",
    paddingTop: "7px",
    paddingBottom: "7px",
  },
};

function SearLogo() {
  return (
    <Box
      sx={{
        textAlign: "center",
        color: "#ffffff",
        mb: "18px",
      }}
    >
      <Typography
        component="div"
        sx={{
          fontSize: { xs: "56px", sm: "64px" },
          fontWeight: 800,
          letterSpacing: "1px",
          lineHeight: 1,
          textShadow: "0 2px 10px rgba(0, 42, 135, 0.15)",
        }}
      >
        SEAR
      </Typography>

      <Box
        sx={{
          width: "260px",
          maxWidth: "100%",
          height: "1px",
          backgroundColor: "rgba(255, 255, 255, 0.5)",
          mx: "auto",
          my: "6px",
        }}
      />

      <Typography
        sx={{
          fontSize: { xs: "12px", sm: "14px" },
          lineHeight: 1.3,
        }}
      >
        Sistema Eletrônico de Atendimento Remoto
      </Typography>
    </Box>
  );
}

export default function EsqueciSenha() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const [aviso, setAviso] = useState({
    aberto: false,
    mensagem: "",
    severidade: "success",
  });

  const mostrarAviso = (mensagem, severidade) => {
    setAviso({
      aberto: true,
      mensagem,
      severidade,
    });
  };

  const fecharAviso = (_, motivo) => {
    if (motivo === "clickaway") return;

    setAviso((atual) => ({
      ...atual,
      aberto: false,
    }));
  };

  const handleRecuperarSenha = async (event) => {
    event.preventDefault();

    if (loading) return;

    const emailInformado = email.trim();

    if (!emailInformado) {
      mostrarAviso("Informe seu e-mail.", "error");
      return;
    }

    setLoading(true);

    try {
      await axios.post(api.forgotPassword(), {
        email: emailInformado,
      });

      mostrarAviso(
        "Se houver uma conta vinculada a esse e-mail, você receberá as instruções de recuperação. Verifique também o spam e o lixo eletrônico.",
        "success",
      );
    } catch (error) {
      const mensagem = error.response?.data?.msg;

      mostrarAviso(
        typeof mensagem === "string" && mensagem.trim()
          ? mensagem
          : "Não foi possível solicitar a recuperação de senha. Tente novamente.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <IdentidadeVisual variante="acesso">
      <Box
        sx={{
          width: "100%",
          maxWidth: "380px",
          mx: "auto",
          pb: { xs: 0, sm: 7 },
        }}
      >
        <SearLogo />

        <Box
          component="main"
          aria-labelledby="titulo-recuperacao"
          sx={{
            width: "100%",
            p: "22px",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.85)",
            background:
              "linear-gradient(145deg, #ffffff 0%, #f9fcff 60%, #edf5ff 100%)",
            boxShadow: "0 16px 36px rgba(0, 30, 104, 0.28)",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "#e0ecff",
            }}
          >
            <LockOutlined
              sx={{
                color: AZUL_RECUPERACAO,
                fontSize: 32,
              }}
            />
          </Box>

          <Typography
            id="titulo-recuperacao"
            component="h1"
            sx={{
              mb: "12px",
              textAlign: "center",
              color: "#1b2149",
              fontSize: "24px",
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            Recuperar acesso
          </Typography>

          <Typography
            sx={{
              mb: "24px",
              textAlign: "center",
              color: "#46516a",
              fontSize: "14px",
              lineHeight: 1.35,
            }}
          >
            Informe seu e-mail para receber as instruções de redefinição de
            senha.
          </Typography>

          <Box
            component="form"
            onSubmit={handleRecuperarSenha}
            aria-busy={loading}
            sx={{
              display: "grid",
              gap: "14px",
            }}
          >
            <TextField
              id="recuperacao-email"
              name="email"
              type="email"
              label="E-mail"
              placeholder="seu@email.com"
              autoComplete="email"
              fullWidth
              required
              disabled={loading}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined
                      sx={{
                        color: AZUL_RECUPERACAO,
                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={campoEmailSx}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
              sx={{
                height: 44,
                borderRadius: "7px",
                backgroundColor: AZUL_RECUPERACAO,
                color: "#ffffff",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,

                "&:hover": {
                  backgroundColor: "#0053d1",
                },
              }}
            >
              {loading ? "Enviando..." : "Enviar instruções"}
            </Button>
          </Box>

          <Button
            component={RouterLink}
            to="/login"
            variant="outlined"
            fullWidth
            sx={{
              mt: "12px",
              minHeight: 30,
              height: 30,
              py: 0,
              borderRadius: "6px",
              borderColor: "#78a3ff",
              color: AZUL_RECUPERACAO,
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 600,

              "&:hover": {
                borderColor: AZUL_RECUPERACAO,
                backgroundColor: "#edf5ff",
              },
            }}
          >
            Voltar para o login
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={aviso.aberto}
        autoHideDuration={6000}
        onClose={fecharAviso}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={fecharAviso}
          severity={aviso.severidade}
          sx={{ width: "100%" }}
        >
          {aviso.mensagem}
        </Alert>
      </Snackbar>
    </IdentidadeVisual>
  );
}
